import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { tenderAPI } from '../services/api';

/**
 * 經銷商「價格查看與領標確認」公開頁面
 * 透過加密 token 識別身份，不需登入
 */
function TenderConfirmPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [hasCollectedTender, setHasCollectedTender] = useState(false);
  const [hasConfirmedPrice, setHasConfirmedPrice] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await tenderAPI.getTenderPage(token);
      setData(res.data);
      if (res.data.already_confirmed) {
        setSubmitted(true);
      }
    } catch (err) {
      setError('連結無效或已過期');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!hasCollectedTender || !hasConfirmedPrice) {
      alert('請勾選兩項確認後再送出');
      return;
    }
    try {
      setSubmitting(true);
      await tenderAPI.submitTenderConfirm(token, {
        has_collected_tender: hasCollectedTender,
        has_confirmed_price: hasConfirmedPrice,
      });
      setSubmitted(true);
    } catch (err) {
      alert('送出失敗，請稍後再試');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <p style={{ textAlign: 'center', fontSize: 18 }}>載入中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={{ color: '#c62828', textAlign: 'center' }}>{error}</h2>
          <p style={{ textAlign: 'center', color: '#666' }}>
            如有問題請洽 周伶瑋 (Jolie) 02-8773-0263 #181
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successBox}>
            <h2 style={{ color: '#2e7d32', margin: 0 }}>確認完成！</h2>
            <p style={{ marginTop: 12, fontSize: 16 }}>
              感謝 <strong>{data?.dealer?.company}</strong> 的確認。
            </p>
            <p>「價格承諾書 (PDF)」已寄送至您的 Email，請查收。</p>
            <p style={{ color: '#888', fontSize: 14, marginTop: 20 }}>
              如有任何疑問，請洽 周伶瑋 (Jolie) 02-8773-0263 #181
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { campaign, dealer, products } = data;

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>招標填報確認</h1>
          <p style={styles.headerSub}>宇聯科技股份有限公司</p>
        </div>

        {/* Info Section */}
        <div style={styles.body}>
          <div style={styles.infoBox}>
            <h3 style={{ margin: '0 0 8px' }}>招標資訊</h3>
            <p><strong>案號：</strong>{campaign.case_number}</p>
            <p><strong>案名：</strong>{campaign.title}</p>
            <p><strong>經銷商：</strong>{dealer.company}</p>
            <p><strong>聯絡人：</strong>{dealer.name}</p>
          </div>

          {/* Warning */}
          <div style={styles.warningBox}>
            <strong>請務必依照下表之「填報單價(含稅)」進行系統報價，填錯將導致廢標！</strong>
          </div>

          {/* Price Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={{ ...styles.th, width: 60, textAlign: 'center' }}>項次</th>
                  <th style={styles.th}>產品品名</th>
                  <th style={{ ...styles.th, width: 100, textAlign: 'center' }}>代理品牌</th>
                  <th style={{ ...styles.th, width: 130, textAlign: 'right' }}>填報單價(含稅)</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, idx) => (
                  <tr key={idx} style={idx % 2 === 0 ? {} : { background: '#fafafa' }}>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{p.item_number}</td>
                    <td style={styles.td}>{p.product_name}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{p.brand_name || '-'}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 'bold', color: '#c62828' }}>
                      {p.price ? `NT$ ${p.price.toLocaleString()}` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Checkboxes */}
          <div style={styles.confirmSection}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={hasCollectedTender}
                onChange={(e) => setHasCollectedTender(e.target.checked)}
                style={styles.checkbox}
              />
              <span>我已前往政府電子採購網領標</span>
            </label>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={hasConfirmedPrice}
                onChange={(e) => setHasConfirmedPrice(e.target.checked)}
                style={styles.checkbox}
              />
              <span>我已確認報價金額與宇聯規定一致</span>
            </label>
          </div>

          {/* Submit Button */}
          <div style={{ textAlign: 'center', margin: '30px 0' }}>
            <button
              onClick={handleSubmit}
              disabled={submitting || !hasCollectedTender || !hasConfirmedPrice}
              style={{
                ...styles.submitBtn,
                opacity: (!hasCollectedTender || !hasConfirmedPrice) ? 0.5 : 1,
                cursor: (!hasCollectedTender || !hasConfirmedPrice) ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? '送出中...' : '送出確認'}
            </button>
          </div>

          <p style={{ textAlign: 'center', fontSize: 13, color: '#999' }}>
            點擊「送出確認」後，系統將自動發送「價格承諾書 (PDF)」至您的 Email
          </p>

          {/* Contact */}
          <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '30px 0' }} />
          <p style={{ fontSize: 14, color: '#666', lineHeight: 1.8 }}>
            如有任何疑問，請洽專案連絡人：<br />
            周伶瑋 (Jolie) 02-8773-0263 #181 / jolie.chou@ulink.com.tw<br />
            蘇鈺涵 (Anny) 02-8773-0263 #182 / anny.su@ulink.com.tw
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f0f2f5',
    padding: '20px',
    fontFamily: "'Microsoft JhengHei', 'Noto Sans TC', Arial, sans-serif",
  },
  card: {
    maxWidth: 800,
    margin: '0 auto',
    background: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
  },
  header: {
    background: 'linear-gradient(135deg, #b71c1c, #e53935)',
    padding: '30px',
    textAlign: 'center',
  },
  headerTitle: {
    color: '#fff',
    margin: 0,
    fontSize: 24,
  },
  headerSub: {
    color: '#ffcdd2',
    margin: '8px 0 0',
    fontSize: 14,
  },
  body: {
    padding: '30px',
  },
  infoBox: {
    background: '#e3f2fd',
    padding: 16,
    borderRadius: 6,
    marginBottom: 20,
    borderLeft: '4px solid #1565c0',
  },
  warningBox: {
    background: '#ffebee',
    padding: 16,
    borderRadius: 6,
    marginBottom: 20,
    borderLeft: '4px solid #c62828',
    color: '#c62828',
    fontSize: 15,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    border: '1px solid #e0e0e0',
  },
  tableHeader: {
    background: '#e3f2fd',
  },
  th: {
    padding: '10px 12px',
    textAlign: 'left',
    borderBottom: '2px solid #1565c0',
    fontSize: 14,
  },
  td: {
    padding: '8px 12px',
    borderBottom: '1px solid #eee',
    fontSize: 14,
  },
  confirmSection: {
    background: '#f5f5f5',
    padding: 20,
    borderRadius: 6,
    marginTop: 24,
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 16,
    marginBottom: 12,
    cursor: 'pointer',
  },
  checkbox: {
    width: 20,
    height: 20,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '16px 60px',
    background: 'linear-gradient(135deg, #c62828, #e53935)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 18,
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(198,40,40,0.4)',
  },
  successBox: {
    textAlign: 'center',
    padding: '60px 30px',
  },
};

export default TenderConfirmPage;
